import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ApiResult, LoadingStatus } from "../lib/types";
import axios from "axios";
import { PURGE } from "redux-persist";
import { apiPost } from "../components/Common/Axios";
import { User } from "lib/models";

const name = "user";

export type UserState = {
  me: User;
  id: number;
  email: string;
  menuIndex: number;
  name: string;
  role: number;
  accessToken: string;
  refreshToken: string;
  loginStatus: number;
  apiResult: number;
};

const initialState: UserState = {
  me: {
    id: 0,
    email: "",
    name: "",
    role: 0,
  },
  id: 0,
  email: "",
  name: "",
  role: 0,
  accessToken: "",
  refreshToken: "",
  menuIndex: 0,
  loginStatus: 0,
  apiResult: 0,
};

///--------------------------------------------
/// Login
///--------------------------------------------
export interface LoginFormValues {
  userid: string;
  password: string;
}

export const asyncLogin = createAsyncThunk(
  `${name}/login`,
  async (formValues: LoginFormValues, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVER}/api/user/login`,
        formValues
      );

      // 로그인 오류
      if (response.data.error) {
        // alert("아이디 또는 비밀번호가 틀렸습니다");
        return thunkAPI.rejectWithValue(ApiResult.NOT_EXIST);
      }

      return response.data.data as UserState;
    } catch (error) {
      console.error(`asyncLogin Error ${error}`);
      return thunkAPI.rejectWithValue(ApiResult.UNKNOWN_ERROR);
    }
  }
);

///--------------------------------------------
/// SignUp
///--------------------------------------------
export interface SignUpFormValues {
  userid: string;
  username: string;
  email: string;
  password: string;
  password2: string;
  group: string;
}

export const asyncSignup = createAsyncThunk(
  `${name}/signup`,
  async (formValues: SignUpFormValues, thunkAPI) => {
    try {
      const payload = {
        userid: formValues.userid,
        username: formValues.username,
        email: formValues.email,
        password: formValues.password,
        group: formValues.group,
      };

      const data = await apiPost("/auth/signup", payload);

      // 이미 존재하는 ID
      if (data.error) {
        alert("이미 존재하는 아이디 입니다");
        return thunkAPI.rejectWithValue(ApiResult.ALREADY_EXIST);
      }

      return data as UserState;
    } catch (error) {
      console.error(`asyncSignup Error ${error}`);
      return thunkAPI.rejectWithValue(ApiResult.UNKNOWN_ERROR);
    }
  }
);

///--------------------------------------------
/// Slice
///--------------------------------------------
export const userSlice = createSlice({
  name,
  initialState,
  reducers: {
    // setUser: (state, action: PayloadAction<User>) => {
    //   state.user = action.payload
    // },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    setMenuIndex: (state, action: PayloadAction<number>) => {
      state.menuIndex = action.payload;
    },
    restoreSession: (state) => {
      const accessToken = sessionStorage.getItem("accessToken");
      const refreshToken = sessionStorage.getItem("refreshToken");

      if (accessToken && refreshToken) {
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        // 토큰이 있으면 로그인 상태로 설정
        state.loginStatus = LoadingStatus.FULFILLED;
      }
    },
  },
  extraReducers: (builder) => {
    // login
    builder.addCase(asyncLogin.pending, (state, action) => {
      state.loginStatus = LoadingStatus.PENDING;
    });
    builder.addCase(
      asyncLogin.fulfilled,
      (state, action: PayloadAction<UserState>) => {
        if (!action.payload) {
          state.loginStatus = LoadingStatus.REJECTED;
          return;
        }
        state.me = {
          id: action.payload.id,
          email: action.payload.email,
          name: action.payload.name,
          role: action.payload.role,
        };
        state.id = action.payload.id;
        state.email = action.payload.email;
        state.name = action.payload.name;
        state.role = action.payload.role;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.loginStatus = LoadingStatus.FULFILLED;

        sessionStorage.setItem("accessToken", action.payload.accessToken);
        sessionStorage.setItem("refreshToken", action.payload.refreshToken);
      }
    );
    builder.addCase(asyncLogin.rejected, (state, action) => {
      state.apiResult = action.payload as number;
      state.loginStatus = LoadingStatus.REJECTED;
    });

    // logout
    builder.addCase(PURGE, () => initialState);
  },
});

export const { setAccessToken, setMenuIndex, restoreSession } =
  userSlice.actions;
export default userSlice.reducer;
